import { StatusBadge } from './ui';
import { useId } from 'react';
import type { ApplicationCapability, CapabilityDrift, ComponentPlan, PlanResourceEstimate } from '../api';
import { Callout } from './primitives';
import type { CalloutTone } from './primitives';
import {
  capabilityConfidenceLabel,
  capabilityEvidenceSourceLabel,
  capabilityStatusLabelFor,
  compatibilityLabel,
  nativeSupportLabel,
  repositorySupportLabel,
  searchDecisionLabel,
  searchImplementationLabel,
} from '../labels';

interface CatalogSearchSectionProps {
  capability: ApplicationCapability;
  /** The plan's catalog_search component, when the section shows a plan. */
  component?: ComponentPlan | null;
  estimate?: PlanResourceEstimate | null;
  drift?: CapabilityDrift[];
  /** Compact: no evidence disclosure, no host size (environment page). */
  compact?: boolean;
}


/** externalServiceText says what the code needs from outside the application. */
function externalServiceText(cap: ApplicationCapability): string {
  if (cap.status !== 'detected') return 'Not determined';
  if (!cap.external_service_required) return 'Not required';
  const kind = cap.external_service_kind === 'elasticsearch' ? 'Elasticsearch' : cap.external_service_kind === 'opensearch' ? 'OpenSearch' : 'Elasticsearch or OpenSearch';
  return `Required: ${kind}${cap.version_constraint ? ` ${cap.version_constraint}` : ''}`;
}

/** evidenceSourceText names where a fact came from without repeating the
 * path when the source label already is the path. */
function evidenceSourceText(e: { source: string; path?: string }): string {
  const label = capabilityEvidenceSourceLabel[e.source] ?? e.source;
  if (!e.path || e.path === label || label.includes(e.path)) return label;
  return `${label} · ${e.path}`;
}

/** decisionText is the infrastructure consequence in one sentence. */
function decisionText(cap: ApplicationCapability, component?: ComponentPlan | null): { title: string; body: string; tone: CalloutTone } {
  const decision = (component?.details?.decision as string | undefined) ?? (cap.status !== 'detected' ? 'paused' : cap.external_service_required ? 'provision' : 'not_required');
  const title = searchDecisionLabel[decision] ?? decision;
  switch (decision) {
    case 'not_required':
      // The requirement chips say it; a healthy decision adds no sentence.
      return { title, body: '', tone: 'info' };
    case 'paused':
      return {
        title,
        body:
          cap.status === 'conflict'
            ? 'The running store and the repository disagree. Nothing is provisioned or removed until this is reconciled.'
            : 'Commerce Ops could not conclusively identify the search engine. Automatic search infrastructure provisioning is paused for this component until the requirement is resolved: nothing is installed, replaced or changed.',
        tone: 'warning',
      };
    case 'reuse':
      return { title, body: component?.reason ?? '', tone: 'info' };
    case 'integrate':
      return { title, body: component?.reason ?? '', tone: 'info' };
    case 'operator_override':
      return { title, body: component?.reason ?? 'The environment’s Magento setup overrides the discovered capability.', tone: 'warning' };
    default:
      return { title, body: component?.reason ?? '', tone: 'info' };
  }
}

/**
 * CatalogSearchSection shows what the code runs its catalog search on and
 * what Commerce Ops concludes from it. The engine is discovered from the
 * repository (and confirmed by the running store), never assumed from
 * "it is Magento"; every fact carries its evidence.
 */
export function CatalogSearchSection({ capability: cap, component, estimate, drift, compact }: CatalogSearchSectionProps) {
  const decision = decisionText(cap, component);
  const requirements: { label: string; required: boolean | null }[] = [
    { label: 'OpenSearch', required: cap.status !== 'detected' ? null : cap.external_service_required && cap.external_service_kind !== 'elasticsearch' },
    { label: 'Elasticsearch', required: cap.status !== 'detected' ? null : cap.external_service_required && cap.external_service_kind !== 'opensearch' },
    { label: 'Java for search', required: cap.status !== 'detected' ? null : cap.external_service_required },
  ];
  const searchDrift = (drift ?? []).find((d) => d.key === 'catalog_search');
  const implementationDetail = [cap.module, cap.package].filter(Boolean).join(' · ');
  // The section appears twice on a project page (plan card and environment
  // card); each needs its own heading id.
  const headingId = useId();

  return (
    <section className="capability" aria-labelledby={headingId} data-testid="catalog-search-section">
      <div className="capability__header">
        <h3 id={headingId} className="capability__title">
          Catalog search
        </h3>
        <span data-testid="catalog-search-status">
          <StatusBadge
            kind="capabilityDetection"
            value={cap.status === 'detected' && cap.confidence === 'low' ? 'detected_low_confidence' : cap.status}
            label={capabilityStatusLabelFor(cap)}
          />
        </span>
        {cap.status === 'detected' ? (
          <span className="type-meta" data-testid="catalog-search-confidence">
            {capabilityConfidenceLabel[cap.confidence] ?? cap.confidence}
            {cap.runtime?.agrees_with_static ? ' · confirmed on the running store' : ''}
          </span>
        ) : null}
      </div>

      <div className="capability__facts">
        <div>
          <span className="capability__label">Engine</span>
          <strong data-testid="catalog-search-engine">{cap.status === 'unresolved' && !cap.engine_identifier ? 'Not determined' : cap.provider_name}</strong>
          {cap.engine_identifier ? (
            <span className="capability__sub">
              catalog/search/engine = <code>{cap.engine_identifier}</code>
            </span>
          ) : null}
        </div>
        <div>
          <span className="capability__label">Implementation</span>
          <strong>{searchImplementationLabel[cap.implementation] ?? cap.implementation}</strong>
          {implementationDetail ? (
            <span className="capability__sub">{implementationDetail}</span>
          ) : null}
        </div>
        <div>
          <span className="capability__label">External service</span>
          <strong data-testid="catalog-search-external">{externalServiceText(cap)}</strong>
        </div>
        <div>
          <span className="capability__label">Magento native support</span>
          <strong>{nativeSupportLabel[cap.magento_native_support] ?? cap.magento_native_support}</strong>
          {cap.magento_version ? <span className="capability__sub">Magento {cap.magento_version}</span> : null}
        </div>
        <div>
          <span className="capability__label">Repository support</span>
          <strong>{repositorySupportLabel[cap.repository_support] ?? cap.repository_support}</strong>
        </div>
        <div>
          <span className="capability__label">Status</span>
          <strong data-testid="catalog-search-compatibility">{compatibilityLabel[cap.compatibility] ?? cap.compatibility}</strong>
        </div>
      </div>
      {cap.compatibility_note && !compact ? (
        <p className="muted capability__note" data-testid="catalog-search-compatibility-note">
          {cap.compatibility_note}
        </p>
      ) : null}

      <Callout
        tone={decision.tone}
        title={`Infrastructure decision: ${decision.title}`}
        testId="catalog-search-decision"
        className="capability__decision"
      >
        <span className="capability__chips" aria-label="Search infrastructure requirements">
          {requirements.map((r) => (
            <span key={r.label} className="badge" data-testid={`requirement-${r.label.toLowerCase().replace(/\s+/g, '-')}`}>
              {r.label}: {r.required === null ? 'undecided' : r.required ? 'required' : 'not required'}
            </span>
          ))}
        </span>
        {compact ? (
          // On the environment card the reasoning is one click away (CO-161).
          cap.compatibility_note || decision.body ? (
            <details className="quiet-disclosure">
              <summary>Why</summary>
              {cap.compatibility_note ? (
                <p className="muted capability__note" data-testid="catalog-search-compatibility-note">
                  {cap.compatibility_note}
                </p>
              ) : null}
              {decision.body ? <p>{decision.body}</p> : null}
            </details>
          ) : null
        ) : decision.body ? (
          <p>{decision.body}</p>
        ) : null}
        {!compact && estimate ? (
          <p className="type-meta" data-testid="catalog-search-sizing">
            Planned stack: about {Math.round(estimate.total_memory_mb / 1024)}&nbsp;GB of memory; a host with {estimate.recommended_host_memory_gb}&nbsp;GB is recommended
            {estimate.search_service_memory_mb > 0 ? `, of which the search service accounts for about ${Math.round(estimate.search_service_memory_mb / 1024)}\u00a0GB` : ' (no search service to size for)'}.
          </p>
        ) : null}
      </Callout>

      {searchDrift ? (
        <Callout tone="danger" title="Architecture change detected" testId="catalog-search-drift">
          Previous analysis: {searchDrift.previous.provider_name}. Current: {searchDrift.current.provider_name}. Infrastructure reconciliation is required;
          nothing on the server changes by itself.
        </Callout>
      ) : null}

      {cap.warnings && cap.warnings.length > 0 ? (
        <ul className="capability__warnings" data-testid="catalog-search-warnings">
          {cap.warnings.map((w, i) => (
            <li key={i}>{w}</li>
          ))}
        </ul>
      ) : null}

      {!compact && cap.evidence && cap.evidence.length > 0 ? (
        <details className="capability__evidence">
          <summary>How Commerce Ops knows ({cap.evidence.length} {cap.evidence.length === 1 ? 'fact' : 'facts'})</summary>
          <ul>
            {cap.evidence.map((e, i) => (
              <li key={i}>
                <span className="muted">{evidenceSourceText(e)}</span>
                <span>{e.detail}</span>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </section>
  );
}
